import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { UIController } from '@/game/GameComponents/PhaserGame/UI/UIController';
import { waitForMilliseconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import {
  DEFAULT_CURSOR_PATH,
  HOVER_CURSOR_PATH,
} from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import _ from 'lodash';

export interface ScrollGroupConfig {
  scene: Phaser.Scene;
  content: Phaser.GameObjects.Container;
  scrollElement: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollSpeedX?: number;
  scrollSpeedY?: number;
  scrollBacking?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollHandle?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  maskMargins?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
}

export class ScrollGroup {
  scene: Phaser.Scene;
  content: Phaser.GameObjects.Container; // Contains all the content that will be scrolled.
  scrollElement: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice; // The element that the user scrolls on to move the scroll group.
  scrollSpeedX: number;
  scrollSpeedY: number;
  dragSpeedY: number;
  dragSpeedX: number;
  scrollBarBacking?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;
  scrollBarHandle?: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;

  originX: number;
  originY: number;
  maxX: number;
  maxY: number;

  maskMargins?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
  maskGraphics?: Phaser.GameObjects.Graphics;
  maskRect?: Phaser.Geom.Rectangle;

  constructor(config: ScrollGroupConfig) {
    this.scene = config.scene;
    this.content = config.content;
    this.scrollElement = config.scrollElement;
    this.scrollSpeedX = config.scrollSpeedX ?? 1;
    this.scrollSpeedY = config.scrollSpeedY ?? 1;
    this.dragSpeedX = this.scrollSpeedX * 10;
    this.dragSpeedY = this.scrollSpeedY * 10;
    this.scrollBarBacking = config.scrollBacking;
    this.scrollBarHandle = config.scrollHandle;
    this.maskMargins = config.maskMargins;

    this.originX = this.content.x;
    this.originY = this.content.y;
    this.maxX = this.content.width;
    this.maxY = this.content.height;

    this.scrollElement.setInteractive({
      cursor: `${DEFAULT_CURSOR_PATH}`,
      draggable: true,
    });
    this.scrollElement.on('wheel', (e: any) => {
      this.handleScroll(e);
    });
    this.scrollElement.on('drag', (e: any) => {
      this.handleDrag(e);
    });

    this.setupChildrenInteractions(this.content);

    if (this.scrollBarHandle) {
      this.scrollBarHandle.setInteractive({
        cursor: `${HOVER_CURSOR_PATH}`,
        draggable: true,
      });
      this.scrollBarHandle.on('drag', (e: any) => {
        this.handleScrollHandleDrag(e);
      });
      this.scrollBarHandle.on('pointerover', () => {
        this.scrollBarHandle?.setScale(4.4);
      });
      this.scrollBarHandle.on('pointerout', () => {
        this.scrollBarHandle?.setScale(4);
      });
    }
    ResponsivenessManager.InitializeResizeHandler(
      this.scene,
      () => this.refresh(),
      this,
    );
  }

  setupChildrenInteractions(container: Phaser.GameObjects.Container) {
    container.list.forEach((item: Phaser.GameObjects.GameObject) => {
      if (item instanceof Phaser.GameObjects.Container) {
        this.setupChildrenInteractions(item);
      }
      if ('controller' in item && item.controller instanceof UIController) {
        item.controller.onDrag = (_controller: UIController, event: any) => {
          this.handleDrag(event);
        };
      }
    });
  }

  backToTop() {
    this.updateScrollViewPosition(0, 0);
  }

  async refresh() {
    // Wait for the next frame to ensure the scene is fully rendered.
    await waitForMilliseconds(this.scene.time, 10);
    this.updateContentParameters();
    this.updateGraphicMask();
    this.updateInteractivityWindows();
  }

  updateContentParameters() {
    // Use this rather than getBounds() as getBounds() includes children
    // with visibility set to false.
    const contentContainer = this.content.list[0];
    if (!contentContainer) return;
    const bounds = this.content.getBounds();
    this.originX = this.content.x;
    this.originY = this.content.y;
    this.maxX = bounds.width;
    const viewHeight = this.scrollElement.getBounds().height;

    this.maxY = Math.max(viewHeight, bounds.height);
  }

  updateGraphicMask() {
    if (!this.scrollElement) return;
    // set up the mask for the grid area.
    const worldMatrix = this.scrollElement.getWorldTransformMatrix();
    const gridPosition = worldMatrix.transformPoint(
      this.scrollElement.x,
      this.scrollElement.y,
    );

    const margins = this.maskMargins;

    const calculatedScaleX =
      this.scrollElement.getWorldTransformMatrix().scaleX;
    const calculatedScaleY =
      this.scrollElement.getWorldTransformMatrix().scaleY;

    const maskRect = new Phaser.Geom.Rectangle(
      gridPosition.x + (margins?.left ?? 0) * calculatedScaleX,
      gridPosition.y + (margins?.top ?? 0) * calculatedScaleY,
      this.scrollElement.getBounds().width -
        (margins?.right ?? 0) * calculatedScaleX,
      this.scrollElement.getBounds().height -
        (margins?.bottom ?? 0) * calculatedScaleY,
    );
    this.maskRect = maskRect;

    if (this.maskGraphics) {
      this.maskGraphics.destroy();
    }

    const maskGraphics = this.scene.make.graphics();
    maskGraphics.fillStyle(0xffffff, 0.2);
    maskGraphics.fillRect(
      maskRect.x,
      maskRect.y,
      maskRect.width,
      maskRect.height,
    );
    this.maskGraphics = maskGraphics;

    const mask = maskGraphics.createGeometryMask();
    this.content.setMask(mask);
  }

  updateInteractivityWindows() {
    this.updateContainerInteractivity(this.content);
  }

  updateContainerInteractivity(container: Phaser.GameObjects.Container) {
    container.list.forEach((item: Phaser.GameObjects.GameObject) => {
      // Skip non-visual objects
      if (
        !('getBounds' in item) ||
        typeof (item as any).getBounds !== 'function'
      )
        return;

      const itemBounds = (item as any).getBounds() as Phaser.Geom.Rectangle;
      const isVisible = Phaser.Geom.Rectangle.Overlaps(
        this.maskRect!,
        itemBounds,
      );

      // Enable or disable interactivity based on visibility
      if (item.input) {
        item.input.enabled = isVisible;
      }

      if (item instanceof Phaser.GameObjects.Container) {
        this.updateContainerInteractivity(item);
      }
    });
  }

  handleScrollHandleDrag(e: any) {
    //const { position, prevPosition } = e;
    if (!this.scrollBarBacking || !this.scrollBarHandle) return;
    const barPosY = this.scrollBarBacking?.y;
    const barPosX = this.scrollBarBacking?.x;
    const matrix = this.scrollBarBacking!.getWorldTransformMatrix();
    const origin = matrix.transformPoint(barPosX, barPosY);

    let newY = e.position.y - origin.y;

    newY = _.clamp(
      newY,
      0,
      this.scrollBarBacking.getBounds().height -
        this.scrollBarHandle.getBounds().height,
    );

    this.scrollBarHandle.y = newY;

    const normalizedNewY =
      newY /
      (this.scrollBarBacking.getBounds().height -
        this.scrollBarHandle.getBounds().height);

    this.updateScrollViewPosition(undefined, normalizedNewY, false);
  }

  handleDrag(e: any) {
    const { position, prevPosition } = e;
    if (!this.scrollBarBacking) return;
    const deltaX = (prevPosition.x - position.x) * this.dragSpeedX;
    const deltaY = (prevPosition.y - position.y) * this.dragSpeedY;
    let newY = _.clamp(
      -this.content.y + deltaY * this.scrollSpeedY,
      0,
      this.maxY,
    );
    let newX = _.clamp(
      this.content.x + deltaX * this.scrollSpeedX,
      0,
      this.maxX,
    );
    const normalizedNewY = newY / this.maxY;
    const normalizedNewX = newX / this.maxX;
    this.updateScrollViewPosition(normalizedNewX, normalizedNewY);
  }

  handleScroll(e: any) {
    const { deltaY, deltaX } = e;
    // use -this.content.y because the content moves up as the user scrolls down.
    let newY = _.clamp(
      -this.content.y + deltaY * this.scrollSpeedY,
      0,
      this.maxY,
    );
    let newX = _.clamp(
      this.content.x + deltaX * this.scrollSpeedX,
      0,
      this.maxX,
    );
    const normalizedNewY = newY / this.maxY;
    const normalizedNewX = newX / this.maxX;
    this.updateScrollViewPosition(normalizedNewX, normalizedNewY);
  }

  updateScrollViewPosition(
    normalizedNewX?: number,
    normalizedNewY?: number,
    updateScrollHandlePosition = true,
  ) {
    normalizedNewY = _.clamp(normalizedNewY ?? 0, 0, 1);
    normalizedNewX = _.clamp(normalizedNewX ?? 0, 0, 1);

    this.scene.tweens.add({
      targets: this.content,
      x: -this.maxX * normalizedNewX,
      y: -this.maxY * normalizedNewY,
      duration: 75,
    });
    this.updateInteractivityWindows();
    if (!this.scrollBarBacking || !this.scrollBarHandle) return;
    if (!updateScrollHandlePosition) return;
    const handleYMin = 0;
    const handleYMax =
      this.scrollBarBacking.getBounds().height -
      this.scrollBarHandle.getBounds().height / 2;
    let handleY = Phaser.Math.Linear(handleYMin, handleYMax, normalizedNewY);
    handleY = _.clamp(handleY, handleYMin, handleYMax);
    this.scene.tweens.add({
      targets: this.scrollBarHandle,
      y: handleY,
      duration: 75,
    });
  }
}
